import catalog from '../database/hourly-rates.seed.json';
import type { ProductSpec } from '../types/product';

export const HOURLY_RATE_CATALOG = catalog;
export const RATE_GROUP_ORDER = [...new Set(catalog.map((rate) => rate.group))];
const normalized = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split('(')[0]
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
const keyFor = (group: string, name: string) =>
  catalog.find((rate) => rate.group === group && rate.name === name)?.key;

/** Resolve only legacy amount keys. Explicitly selected rates are never inferred again. */
export function resolveHourlyRate(
  context: { name: string; category?: string | null },
  line: { description: string; rateKey: string }
): string | null {
  const legacy = /^tarief_(\d+)$/.exec(line.rateKey);
  if (!legacy) return null;
  const cents = Number(legacy[1]) * 100;
  const name = normalized(context.name);
  const description = normalized(line.description);
  const category = normalized(context.category ?? '');
  // These departments have one rate covering their entire service in the supplied overview.
  if (cents === 17000 && name.includes('positionering'))
    return keyFor('Positionering', 'Positioneren') ?? null;
  if (cents === 17000 && name.includes('marketingstrategie'))
    return keyFor('Marketingstrategie', 'Marketingstrategie') ?? null;
  if (/^projectmana(gement|gment)$/.test(description)) {
    if (category === 'video' && cents === 12000)
      return keyFor('Studio', 'Studio - Projectmanagement') ?? null;
    if (category === 'website development' && (cents === 12000 || cents === 13000))
      return (
        keyFor(
          cents === 13000 ? 'Development' : 'Design',
          cents === 13000 ? 'Development - Projectmanagement' : 'Design Projectmanagement'
        ) ?? null
      );
    if (cents === 15000) return keyFor('Marketing', 'Customer success management') ?? null;
    return null;
  }
  const preferred = name.includes('identiteit')
    ? ['Merkidentiteit']
    : category === 'website development'
      ? ['Development', 'Design', 'Marketing']
      : category === 'video'
        ? ['Studio', 'Design']
        : ['Marketing', 'Salesmarketing', 'Studio', 'Design'];
  const candidates = catalog.filter(
    (rate) =>
      rate.amountCents === cents &&
      [rate.name, ...rate.aliases].some((alias) => normalized(alias) === description)
  );
  for (const group of preferred) {
    const candidate = candidates.find((rate) => rate.group === group);
    if (candidate) return candidate.key;
  }
  return candidates.length === 1 ? candidates[0].key : null;
}

export function resolveSpecRateKeys(
  context: { name: string; category?: string | null },
  spec: ProductSpec,
  availableKeys: Set<string>,
  replacements: Record<string, string> = {}
): ProductSpec {
  return {
    ...spec,
    lines: spec.lines.map((line) => {
      let key = resolveHourlyRate(context, line) ?? line.rateKey;
      const seen = new Set<string>();
      while (replacements[key] && !seen.has(key)) {
        seen.add(key);
        key = replacements[key];
      }
      return key && availableKeys.has(key) ? { ...line, rateKey: key } : line;
    }),
  };
}
